import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
} from '@mui/material';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import CoordianatesPicker from '../../components/utils/coordiantesPicker/CoordianatesPicker';
import DropdownMultiSelector from '../../components/utils/dropdownMultiSelector/DropdownMultiSelector';
import SwitchButton from '../../components/utils/MUISpecifics/swithButton/SwitchButton';
import TextInput from '../../components/utils/MUISpecifics/inputText/TextInput';
import PeriodPickerComponent from '../../components/utils/periodPicker/PeriodPickerComponent';
import type { FormElement, FormElementConfigW } from './Types';
import { useFormWithTransformation } from './useFormWithTransformation';
import InputMultiType from '../../components/utils/MUISpecifics/inputMultiType/InputMultiType';
import DropDownItemSelector from '../../components/utils/MUISpecifics/dropDownItemSelector/DropDownItemSelector';
import { useTranslation } from 'react-i18next';

export interface FormGeneratorHandle {
  triggerSubmit: () => void;
}

interface FormGeneratorProps<T extends object> {
  defaultValues: T;
  onSubmit: (i: T) => Promise<void>;
  config: FormElementConfigW<T>[];
  withButton: boolean;
  buttonLabel?: string;
}

const FormGenerator = forwardRef(
  <T extends object>(
    props: FormGeneratorProps<T>,
    ref: React.Ref<FormGeneratorHandle>
  ): JSX.Element => {
    console.log('FormGenerator formConfig:', props.config);
    // console.log('FormGenerator defaultValues:', props.defaultValues);
    const [showPassword, setShowPassword] = React.useState(false);
    const buttonLabel = props.buttonLabel ?? 'Créer';

    const methods = useFormWithTransformation(
      props.defaultValues,
      props.config,
      props.onSubmit
    );
    type Methods = typeof methods;
    const formRef = useRef<HTMLFormElement>(null);
    const {t} = useTranslation(['alerts', 'commons']);

    useImperativeHandle(ref, () => ({
      triggerSubmit: () => {
        return methods.onSubmit({
          preventDefault: () => {},
        } as React.FormEvent<HTMLFormElement>) as unknown as boolean;
      },
    }));

    /**Helper for translations. Universal label resolver for generated forms.
     * If we have the same text with different pretty names,
     * we have a problems. 
     * This helper takes machine keys, if thay exists, and translate them. 
     * In json files we use the same machine keys. 
     *  Priority:
     * 1. fields[0] as i18n key
     * 2. conf.label as fallback
     */
    const resolveLabel = (conf: FormElementConfigW<any>): string => {
      const fieldKey = conf.fields?.[0];

      if (!fieldKey) {
        return conf.label;
      }

      return t(String(fieldKey), {
        // namespace can be overridden later if needed
        ns: ['alerts', 'commons'],
        defaultValue: conf.label,
      });
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
    };

    const handleMouseUpPassword = (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      event.preventDefault();
    };

    

    const createFormElement =
      (methods: Methods) => (conf: FormElementConfigW<T>, index: number) => {
        switch (conf.elementType as FormElement) {
          case 'TexInput':
            return (
              <div>
                <TextInput
                  onChange={(value) => methods.setMultipleValues(index, value)}
                  defaultValue={
                    (props.defaultValues &&
                      (props.defaultValues[conf.fields[0]] as
                        | string
                        | number
                        | undefined)) ??
                    ''
                  }
                  autoComplete="false"
                  label={conf.label}
                  name={`no-autocomplete-${index}-${String(conf.fields[0])}`}
                  placeholder={conf.placeHolder}
                  errors={
                    methods.hasSubmitted
                      ? {
                          tag: 'Submited',
                          errors: methods.getErrors(conf.fields[0]),
                        }
                      : { tag: 'Not submitted' }
                  }
                />
              </div>
            );
          case 'NumberInput':{
            const label = t(`${conf.label}`);
            return (
              <div>                
                <InputMultiType 
                  onChange={(value) => {
                    if (value === '') {
                      methods.reset();
                      return;
                    }
                    const numericValue = Number(value);
                    if (!Number.isNaN(numericValue)) {
                      methods.setMultipleValues(index, numericValue);
                    }
                  }} 
                  type={'number'} 
                  autoComplete='off'
                  inputMode='decimal'
                  label={label}
                  name={`no-autocomplete-${index}-${String(conf.fields[0])}`}
                  // placeholder={conf.placeHolder}
                  errors={
                    methods.hasSubmitted
                      ? {
                          tag: 'Submited',
                          errors: methods.getErrors(conf.fields[0]),
                        }
                      : { tag: 'Not submitted' }
                  }
                  defaultValue={
                    (props.defaultValues &&
                      (props.defaultValues[conf.fields[0]] as
                        | number
                        | undefined)) ??
                    null
                  }
                />
              </div>
            );
          }
          case 'Switch':
            return (
              <div>
                <SwitchButton
                  onChange={() =>
                    methods.setMultipleValues(
                      index,
                      !methods.watch(conf.fields[0]) as boolean
                    )
                  }
                  defaultValue={methods.watch(conf.fields[0]) as boolean}
                  autoComplete="false"
                  label={conf.label}
                  name={`no-autocomplete-${index}-${String(conf.fields[0])}`}
                  placeholder={conf.placeHolder}
                  errors={
                    methods.hasSubmitted
                      ? {
                          tag: 'Submited',
                          errors: methods.getErrors(conf.fields[0]),
                        }
                      : { tag: 'Not submitted' }
                  }
                  checked={methods.watch(conf.fields[0]) as boolean}
                />
              </div>
            );
          case 'Password': {
            const errors = methods.getErrors(conf.fields[0]);
            const hasErrors = errors && errors.length > 0;
            const showError = methods.hasSubmitted && hasErrors;

            return (
              <FormControl sx={{ m: 1 }} variant="outlined" error={showError}>
                <InputLabel htmlFor="outlined-adornment-password">
                  {conf.label}
                </InputLabel>
                <OutlinedInput
                  id="outlined-adornment-password"
                  type={showPassword ? 'text' : 'password'}
                  onChange={(event) =>
                    methods.setMultipleValues(index, event.target.value)
                  }
                  autoComplete="new-password"
                  defaultValue={
                    (props.defaultValues &&
                      (props.defaultValues[conf.fields[0]] as
                        | string
                        | number
                        | undefined)) ??
                    ''
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword
                            ? 'hide the password'
                            : 'display the password'
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={conf.label}
                  error={showError}
                />

                {showError && <FormHelperText>{errors}</FormHelperText>}
              </FormControl>
            );
          }

          case 'MultiSelector':
            return (
              <div>
                <DropdownMultiSelector
                  onSelect={(values) =>
                    methods.setMultipleValues(index, values)
                  }
                  label={conf.label}
                  values={conf.selectionEntries!}
                  defaultValues={conf.defaultSelectionEntries}
                />
                <p style={{ color: 'red' }} className="form-danger warning">
                  {methods.getErrors(conf.fields[0]!).length > 0 && (
                    <>
                      <span uk-icon="warning"></span>
                      {methods.getErrors(conf.fields[0]!).join(', ')}
                    </>
                  )}
                </p>
              </div>
            );
          case 'DropdownItemSelector':
            return (              
              <div>
                <DropDownItemSelector 
                  onSelect={
                    (value) => methods.setMultipleValues(index, value!)
                  } 
                  values={conf.selectionEntries!}
                  label={conf.label}
                  disabled={false} 
                  name={'select'}   
                  required={conf.required}
                  defaultValue={conf.defaultSelectionEntries![0] ?? null}
                  errors={{
                    tag: methods.getErrors(conf.fields[0]!).length > 0 ? 'Submited' : 'Not submitted',
                    errors: methods.getErrors(conf.fields[0]!)
                  }}        
                />
              </div>
            );
          case 'PeriodPicker':
            return (
              <div>
                <PeriodPickerComponent
                  handleDateUpdate={(value) =>
                    methods.setMultipleValues(index, value!)
                  }
                  label={conf.label}
                  defaultValues={
                    conf.defaultSelectionEntries?.map((v) =>
                      typeof v === 'string' ? v : new Date(v).toISOString()
                    ) as [string, string]
                  }
                  handleOnClean={() => {}}
                  errors={{ tag: 'Not submitted' }}
                />
              </div>
            );
          case 'CoordinatePicker':
            return (
              <div>
                <CoordianatesPicker
                  onClick={(value) => methods.setMultipleValues(index, value)}
                  label={''}
                  context=""
                  errors={{ tag: 'Not submitted' }}
                />
                <p style={{ color: 'red' }} className="form-danger warning">
                  {methods.getErrors(conf.fields[0]!).length > 0 && (
                    <>
                      <span uk-icon="warning"></span>
                      {methods.getErrors(conf.fields[0]!).join(', ')}
                    </>
                  )}
                </p>
              </div>
            );
          default:
            return null;
        }
      };

    return (
      <form onSubmit={methods.onSubmit} ref={formRef} autoComplete="off">
        {/* Champ caché pour "piéger" l'autocomplétion */}
        <input
          type="text"
          name="fakeusernameremembered"
          style={{ display: 'none' }}
          autoComplete="username"
          tabIndex={-1}
          aria-hidden="true"
        />
        <Stack spacing={2}>
          {props.config.map(createFormElement(methods))}
          {props.withButton && (
            <button type="submit" 
              className="uk-button uk-button-default" 
              style={{width: 'fit-content', alignSelf: 'center'}}>
              {methods.watchStatus() === 'loading' ? 'Envoi...' : buttonLabel}
            </button>
          )}  
        </Stack>
        
      </form>
    );
  }
);

export default FormGenerator;
